/**
 * 9-max seat geometry for the poker-table SpotCard.
 *
 * Hero is always anchored at the bottom of the oval (6 o'clock). The
 * other 8 positions wrap around clockwise — preserving poker action
 * order (UTG → UTG+1 → ... → BTN → SB → BB → UTG) regardless of which
 * position hero is dealt.
 *
 * All functions are pure (no React, no DOM); rendering layers translate
 * these percentages into absolute-positioned divs over the oval container.
 */

/** Position labels in clockwise action order — UTG acts first preflop
 *  (after blinds), BB acts last (before the post-blind action returns).
 *  This is the canonical order; rotateSeats rotates it so hero sits at
 *  index 0.
 *
 *  Mirrored in `backend/app/drill_meta.py:CANONICAL_POSITIONS`. The two
 *  must agree — the LLM is told to use these exact strings in both
 *  `spot.position` and `spot.history[].actor` (alongside the `"Hero"`
 *  sentinel). Bad labels are rejected by the parser on the way in
 *  (`messageSegments.ts:isValidSpot`). */
export const POSITIONS_CLOCKWISE = [
  "UTG",
  "UTG1",
  "MP",
  "LJ",
  "HJ",
  "CO",
  "BTN",
  "SB",
  "BB",
] as const;

export type Position = (typeof POSITIONS_CLOCKWISE)[number];

const CANONICAL_POSITION_SET: ReadonlySet<string> = new Set(POSITIONS_CLOCKWISE);

/** True if `label` is one of the 9 canonical seat labels. The `"Hero"`
 *  history-actor sentinel is NOT a position — callers walking history
 *  should special-case it before calling this. */
export function isCanonicalPosition(label: string): label is Position {
  return CANONICAL_POSITION_SET.has(label);
}

export const SEAT_COUNT = 9;

/** Hero seat sits at 90° in CSS coordinates (y-down convention: 90° = bottom).
 *  Other seats placed at HERO_ANGLE_DEG + i * (360 / SEAT_COUNT), going
 *  clockwise visually. */
export const HERO_ANGLE_DEG = 90;
const ANGLE_STEP_DEG = 360 / SEAT_COUNT;

/** Oval half-axes as a percentage of the container's bounding box.
 *  The container has aspect-ratio ~1.6:1, so vertical radius is smaller
 *  than horizontal. Tuned so seat pills sit slightly INSIDE the visible
 *  oval border, not on top of it. */
export const SEAT_RADIUS_X_PCT = 45;
export const SEAT_RADIUS_Y_PCT = 38;

/** Chip stacks render at a fraction of the seat radius — between the
 *  seat and the table center, the way chips physically sit on the felt. */
export const CHIP_RADIUS_RATIO = 0.62;

/**
 * Reorder the 9 positions so `heroPosition` sits at index 0, with the
 * rest following in clockwise action order. Unknown positions return
 * the canonical order unchanged (defensive — should not happen).
 *
 * Examples:
 *   rotateSeats("BB")  → ["BB", "UTG", "UTG1", "MP", "LJ", "HJ", "CO", "BTN", "SB"]
 *   rotateSeats("BTN") → ["BTN", "SB", "BB", "UTG", "UTG1", "MP", "LJ", "HJ", "CO"]
 *   rotateSeats("UTG") → ["UTG", "UTG1", "MP", "LJ", "HJ", "CO", "BTN", "SB", "BB"]
 *
 * If `heroPosition` is not canonical (should be impossible at runtime
 * — the message parser rejects bad spots upstream — but we keep the
 * defensive fallback), warns to the console so the drift is visible.
 */
export function rotateSeats(heroPosition: string): Position[] {
  const idx = POSITIONS_CLOCKWISE.indexOf(heroPosition as Position);
  if (idx < 0) {
    console.warn(
      `[seatLayout] non-canonical hero position ${JSON.stringify(heroPosition)}: falling back to unrotated order`,
    );
    return [...POSITIONS_CLOCKWISE];
  }
  return [
    ...POSITIONS_CLOCKWISE.slice(idx),
    ...POSITIONS_CLOCKWISE.slice(0, idx),
  ];
}

/**
 * Angle (in degrees) for the seat at the given index in the rotated
 * sequence. Index 0 (hero) is always 90° (bottom). Subsequent indices
 * step clockwise.
 *
 * Returned angle is in CSS coordinates (y grows downward, so 90° = down).
 * May exceed 360° for higher indices — the trig functions handle wraparound;
 * callers shouldn't need to mod.
 */
export function seatAngleDeg(seatIndex: number): number {
  return HERO_ANGLE_DEG + seatIndex * ANGLE_STEP_DEG;
}

/**
 * Top/left percentages (0–100) for a seat's center, measured from the
 * top-left of the table container. The container is the bounding box
 * of the oval; seats are placed via absolute positioning + a `transform:
 * translate(-50%, -50%)` on the element itself.
 *
 * Example: seat 0 (hero) → { left: 50, top: 88 } (bottom-center).
 */
export function seatPositionPct(seatIndex: number): {
  left: number;
  top: number;
} {
  const rad = (seatAngleDeg(seatIndex) * Math.PI) / 180;
  return {
    left: 50 + SEAT_RADIUS_X_PCT * Math.cos(rad),
    top: 50 + SEAT_RADIUS_Y_PCT * Math.sin(rad),
  };
}

/**
 * Top/left percentages for a chip stack sitting in front of the seat at
 * `seatIndex`. Same angle as the seat, but pulled inward by
 * CHIP_RADIUS_RATIO. Roughly mimics physical chip placement on a real
 * table felt — between the player and the pot.
 */
export function chipPositionPct(seatIndex: number): {
  left: number;
  top: number;
} {
  const rad = (seatAngleDeg(seatIndex) * Math.PI) / 180;
  return {
    left: 50 + SEAT_RADIUS_X_PCT * CHIP_RADIUS_RATIO * Math.cos(rad),
    top: 50 + SEAT_RADIUS_Y_PCT * CHIP_RADIUS_RATIO * Math.sin(rad),
  };
}

/**
 * Index of the BTN seat in the rotated sequence — useful for placing
 * the dealer-button "D" icon next to the BTN seat regardless of hero's
 * position. Returns 0 if hero IS BTN.
 */
export function dealerButtonSeatIndex(heroPosition: string): number {
  const rotated = rotateSeats(heroPosition);
  return rotated.indexOf("BTN");
}
